import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider, useNavigate } from 'react-router-dom';

import { TezusProvider, TezusContext } from '../components/context.jsx';
import { FEATURE_IMPERSONATION, USE_REAL_BILLING_API, saIsRealTenantsMode } from '../components/mvp-config.jsx';
import {
  authService,
  formatBillingStatusError,
  formatOperationalStatusError,
  readAuthState,
  tenantService,
  _notifStore,
} from '../components/services.jsx';
import { normStatus } from '../components/store-status.js';
import { LoginRoute, RequireAuth } from './router/guards/AuthBoundary.jsx';
import { ProtectedAppLayout } from './layout/ProtectedAppLayout.jsx';
import { RootRedirect } from './router/root-redirect.jsx';
import { createAppRouter } from './router/router.jsx';
import { LegacyRedirect } from './router/route-config.jsx';
import { resolveTenantIdForApi } from './router/paths.js';

const { useState, useEffect, Component, useCallback, useMemo } = React;

const AppSessionContext = React.createContext(null);

export function useAppSession() {
  const ctx = React.useContext(AppSessionContext);
  if (!ctx) throw new Error('useAppSession requires AppSessionProvider');
  return ctx;
}

const clearSaSession = () => {
  try {
    if (authService?.clearSession) authService.clearSession();
    else localStorage.removeItem('tezus-sa-auth');
  } catch (e) { /* ignore */ }
};

class SaErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error) {
    console.error('[Super Admin]', error);
  }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-subtle)', padding: 24 }}>
        <div style={{ maxWidth: 420, width: '100%', background: 'var(--bg-elevated)', borderRadius: 12, padding: 28, boxShadow: 'var(--shadow-lg)', textAlign: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Não foi possível carregar o Super Admin</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20, lineHeight: 1.5 }}>
            Ocorreu um erro inesperado na interface. Limpe a sessão e tente novamente.
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="btn ghost" type="button" onClick={() => { clearSaSession(); try { localStorage.removeItem('tezus-route'); } catch (e) {} window.location.reload(); }}>Limpar sessão</button>
            <button className="btn primary" type="button" onClick={() => window.location.reload()}>Recarregar</button>
          </div>
        </div>
      </div>
    );
  }
}

function AppSessionProvider({ children }) {
  const {
    stores, updateStore, addStore, refetchStores,
    storesLoading, storesError,
    internalUsers, setInternalUsers, auditLog, logAction,
  } = React.useContext(TezusContext);

  const [theme, setTheme] = useState(() => localStorage.getItem('tezus-theme') || 'light');
  const [toast, setToast] = useState('');
  const [authChecking, setAuthChecking] = useState(true);
  const [loggedIn, setLoggedIn] = useState(false);
  const [authError, setAuthError] = useState('');
  const [authUser, setAuthUser] = useState(() => (
    typeof readAuthState === 'function' ? readAuthState()?.user : null
  ) || null);

  const showToast = useCallback((msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2800);
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem('tezus-theme', theme);
  }, [theme]);

  useEffect(() => {
    let mounted = true;
    const finish = (result) => {
      if (!mounted) return;
      if (result?.authenticated) {
        setLoggedIn(true);
        setAuthError('');
        setAuthUser(result.user || (typeof readAuthState === 'function' ? readAuthState()?.user : null) || null);
      } else if (result?.reason === 'forbidden') {
        setAuthError('Seu usuário não tem permissão para acessar o Super Admin.');
        setLoggedIn(false);
        setAuthUser(null);
      } else if (result?.reason === 'network') {
        setAuthError('Não foi possível conectar ao servidor. Verifique se a API está no ar.');
        setLoggedIn(false);
        setAuthUser(null);
      } else {
        setAuthError('');
        setLoggedIn(false);
        setAuthUser(null);
      }
      setAuthChecking(false);
    };
    if (!authService?.bootstrapSession) {
      finish({ authenticated: false, reason: null });
      return () => { mounted = false; };
    }
    authService.bootstrapSession()
      .then(finish)
      .catch(() => {
        clearSaSession();
        finish({ authenticated: false, reason: 'network' });
      });
    return () => { mounted = false; };
  }, []);

  const handleLogout = useCallback(async () => {
    await authService.logout();
    setLoggedIn(false);
    setAuthError('');
    setAuthUser(null);
  }, []);

  const handleLogin = useCallback(async ({ email, password }) => {
    if (!authService?.login) {
      const err = new Error('Auth indisponível');
      err.status = 0;
      throw err;
    }
    await authService.login({ email, password });
    setLoggedIn(true);
    setAuthError('');
    setAuthUser(
      (typeof readAuthState === 'function' ? readAuthState()?.user : null) || null
    );
    try { if (refetchStores) await refetchStores(); } catch (e) { /* ignore */ }
    try { if (_notifStore?.reload) await _notifStore.reload(); } catch (e) { /* ignore */ }
  }, [refetchStores]);

  const handleOpenProfile = useCallback(async () => {
    let user = authUser || (typeof readAuthState === 'function' ? readAuthState()?.user : null);
    if (!user?.email && authService?.me) {
      try {
        const me = await authService.me();
        user = me?.user || null;
        if (user) setAuthUser(user);
      } catch (e) { /* keep existing */ }
    }
    if (!user?.email) {
      showToast('Dados do perfil não disponíveis.');
      return null;
    }
    return user;
  }, [authUser, showToast]);

  const handleSuspend = useCallback((store, setConfirmState) => {
    const currentStore = stores.find((s) => s.id === store.id) || store;
    const ns = typeof normStatus === 'function' ? normStatus(currentStore.status) : currentStore.status;
    const isSuspend = ns !== 'suspended' && ns !== 'suspenso';
    const useRealBilling = typeof USE_REAL_BILLING_API !== 'undefined' && USE_REAL_BILLING_API;
    const tenantId = resolveTenantIdForApi(currentStore);
    setConfirmState({
      title: isSuspend ? `Suspender comercialmente ${currentStore.name}?` : `Reativar comercialmente ${currentStore.name}?`,
      body: isSuspend
        ? <>Altera apenas o <strong>status comercial</strong> (billing). Nesta fase <strong>não bloqueia</strong> automaticamente o acesso da loja.</>
        : <>Restaura o status comercial para ativo. Independente do status operacional.</>,
      confirmLabel: isSuspend ? 'Suspender comercialmente' : 'Reativar comercialmente',
      danger: isSuspend,
      requireReason: useRealBilling,
      minReasonLength: 5,
      onConfirm: async (reason) => {
        if (useRealBilling) {
          try {
            await tenantService.updateStatus(tenantId, {
              status: isSuspend ? 'suspended' : 'active',
              reason: reason || (isSuspend ? 'Suspensão manual' : 'Reativação manual'),
              notes: isSuspend ? 'Suspensão manual pelo Super Admin' : 'Reativação manual pelo Super Admin',
            });
            updateStore(currentStore.id, { status: isSuspend ? 'suspended' : 'active' });
            if (refetchStores) await refetchStores();
            showToast(isSuspend ? `${currentStore.name} suspensa` : `${currentStore.name} ativada`);
            setConfirmState(null);
          } catch (e) {
            const msg = typeof formatBillingStatusError === 'function' ? formatBillingStatusError(e) : (e?.message || 'Erro ao alterar status');
            showToast(msg);
          }
          return;
        }
        const newStatus = isSuspend ? 'suspended' : 'active';
        updateStore(currentStore.id, { status: newStatus, mrr: newStatus === 'suspended' ? 0 : (currentStore.plan?.price || 0) });
        logAction({ acao: isSuspend ? 'Suspendeu loja' : 'Ativou loja', loja: currentStore.name, lojaId: currentStore.id, kind: isSuspend ? 'danger' : 'info' });
        setConfirmState(null);
        showToast(isSuspend ? `${currentStore.name} suspensa (protótipo local)` : `${currentStore.name} ativada (protótipo local)`);
      },
    });
  }, [stores, updateStore, refetchStores, logAction, showToast]);

  const handleOperationalConfirm = useCallback(async ({ reason, operationalModal, setOperationalModal, setOperationalSaving }) => {
    if (!operationalModal) return;
    const { store, action } = operationalModal;
    const tenantId = resolveTenantIdForApi(store);
    setOperationalSaving(true);
    try {
      if (action === 'archive') {
        await tenantService.archive(tenantId, { reason });
      } else {
        await tenantService.reactivate(tenantId, { reason });
      }
      const refreshed = await tenantService.getById(tenantId);
      updateStore(store.id, refreshed.data);
      if (refetchStores) await refetchStores();
      showToast(action === 'archive' ? 'Loja arquivada. Operação bloqueada; dados e domínio preservados.' : 'Loja reativada. Acesso operacional restaurado.');
      logAction({
        acao: action === 'archive' ? 'Arquivou loja (operacional)' : 'Reativou loja (operacional)',
        loja: store.name,
        lojaId: store.id,
        kind: action === 'archive' ? 'danger' : 'info',
      });
      setOperationalModal(null);
    } catch (e) {
      const msg = typeof formatOperationalStatusError === 'function'
        ? formatOperationalStatusError(e)
        : (e?.message || 'Erro ao alterar status operacional');
      showToast(msg);
    } finally {
      setOperationalSaving(false);
    }
  }, [updateStore, refetchStores, logAction, showToast]);

  const handleChangePlanConfirm = useCallback(({ plan, apply, planDrawerStore, setPlanDrawerStore }) => {
    if (planDrawerStore && plan) {
      updateStore(planDrawerStore.id, { plan, mrr: plan.price || planDrawerStore.mrr });
      logAction({ acao: `Alterou plano para ${plan.name}`, loja: planDrawerStore.name, lojaId: planDrawerStore.id, kind: 'info' });
    }
    setPlanDrawerStore(null);
    if (typeof saIsRealTenantsMode === 'function' && saIsRealTenantsMode()) {
      showToast(`Plano alterado para ${plan?.name || '—'}`);
    } else {
      const when = apply === 'immediately' ? 'imediatamente' : 'no próximo ciclo';
      showToast(`Plano alterado para ${plan?.name} (${when})`);
    }
  }, [updateStore, logAction, showToast]);

  const value = useMemo(() => ({
    theme,
    setTheme,
    toast,
    showToast,
    authChecking,
    loggedIn,
    authError,
    authUser,
    setAuthUser,
    handleLogin,
    handleLogout,
    handleOpenProfile,
    shellHandlers: {
      handleSuspend,
      handleOperationalConfirm,
      handleChangePlanConfirm,
      handleOpenProfile,
    },
    stores,
    updateStore,
    addStore,
    refetchStores,
    storesLoading,
    storesError,
    internalUsers,
    auditLog,
    logAction,
  }), [
    theme, toast, showToast, authChecking, loggedIn, authError, authUser,
    handleLogin, handleLogout, handleOpenProfile, handleSuspend, handleOperationalConfirm, handleChangePlanConfirm,
    stores, updateStore, addStore, refetchStores, storesLoading, storesError, internalUsers, auditLog, logAction,
  ]);

  return (
    <AppSessionContext.Provider value={value}>
      {children}
    </AppSessionContext.Provider>
  );
}

function LoginRouteContainer() {
  const { handleLogin, authError, loggedIn } = useAppSession();
  return <LoginRoute onLogin={handleLogin} authError={authError} loggedIn={loggedIn} />;
}

function LogoutRedirector({ children }) {
  const { loggedIn, authChecking } = useAppSession();
  const navigate = useNavigate();
  useEffect(() => {
    if (!authChecking && !loggedIn) {
      // Stay on /login; if somehow on protected tree without auth, RequireAuth handles it.
    }
  }, [loggedIn, authChecking, navigate]);
  return children;
}

function ProtectedLayoutContainer() {
  const session = useAppSession();
  const navigate = useNavigate();

  const onLogout = useCallback(async () => {
    await session.handleLogout();
    navigate('/login', { replace: true });
  }, [session, navigate]);

  return (
    <RequireAuth loggedIn={session.loggedIn} authChecking={session.authChecking}>
      <LogoutRedirector>
        <ProtectedAppLayout
          authUser={session.authUser}
          onLogout={onLogout}
          theme={session.theme}
          setTheme={session.setTheme}
          showToast={session.showToast}
          toast={session.toast}
          shellHandlers={session.shellHandlers}
        />
      </LogoutRedirector>
    </RequireAuth>
  );
}

function RootRedirectContainer() {
  const { loggedIn, authChecking } = useAppSession();
  return (
    <RequireAuth loggedIn={loggedIn} authChecking={authChecking}>
      <RootRedirect />
    </RequireAuth>
  );
}

function LegacyRedirectContainer({ to }) {
  const { loggedIn, authChecking } = useAppSession();
  return (
    <RequireAuth loggedIn={loggedIn} authChecking={authChecking}>
      <LegacyRedirect to={to} />
    </RequireAuth>
  );
}

const router = createAppRouter({
  Login: LoginRouteContainer,
  RootRedirect: RootRedirectContainer,
  ProtectedLayout: ProtectedLayoutContainer,
  LegacyRedirectContainer,
});

function App() {
  return (
    <AppSessionProvider>
      <RouterProvider router={router} />
    </AppSessionProvider>
  );
}

export function mountApp() {
  const rootEl = document.getElementById('root');
  if (!rootEl || !TezusProvider) {
    if (rootEl) {
      rootEl.innerHTML = '<div style="min-height:100vh;display:flex;align-items:center;justify-content:center;padding:24px;font-family:Inter,sans-serif;color:#4b5563"><div style="max-width:420px;text-align:center"><p style="font-weight:600;margin-bottom:8px">Não foi possível carregar o Super Admin</p><p style="font-size:14px;margin-bottom:16px">Scripts da aplicação não iniciaram. Recarregue a página ou limpe o cache.</p><button type="button" onclick="localStorage.removeItem(\'tezus-sa-auth\');localStorage.removeItem(\'tezus-route\');location.reload()" style="padding:8px 16px;border-radius:8px;border:1px solid #d1d5db;background:#fff;cursor:pointer">Limpar sessão e recarregar</button></div></div>';
    }
    return;
  }
  try {
    ReactDOM.createRoot(rootEl).render(
      <SaErrorBoundary>
        <TezusProvider>
          <App />
        </TezusProvider>
      </SaErrorBoundary>,
    );
  } catch (e) {
    console.error('[Super Admin] mount failed', e);
    rootEl.innerHTML = '<div style="min-height:100vh;display:flex;align-items:center;justify-content:center;padding:24px;font-family:Inter,sans-serif"><div style="max-width:420px;text-align:center"><p style="font-weight:600">Erro ao iniciar o Super Admin</p><p style="font-size:14px;color:#6b7280;margin:12px 0">Limpe a sessão e tente novamente.</p><button type="button" onclick="localStorage.removeItem(\'tezus-sa-auth\');localStorage.removeItem(\'tezus-route\');location.reload()" style="padding:8px 16px;border-radius:8px;border:1px solid #d1d5db;background:#fff;cursor:pointer">Limpar sessão</button></div></div>';
  }
}
